import { ImsPermissions } from "@redotech/account-common/permissions";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import BarcodeLabelIcon from "@redotech/redo-icons/bar-chart-01.svg";
import BarcodeLabelIconFilled from "@redotech/redo-icons/bar-chart-01_filled.svg";
import CatalogIcon from "@redotech/redo-icons/catalog.svg";
import CatalogIconFilled from "@redotech/redo-icons/catalog_filled.svg";
import ClipboardCheckIcon from "@redotech/redo-icons/clipboard-check.svg";
import ClipboardCheckIconFilled from "@redotech/redo-icons/clipboard-check_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardIconFilled from "@redotech/redo-icons/dashboard_filled.svg";
import InventoryAdjustmentsIcon from "@redotech/redo-icons/inventory_adjustments.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import LayersIconFilled from "@redotech/redo-icons/layers-two-01_filled.svg";
import ForecastingIcon from "@redotech/redo-icons/line-chart-up-02.svg";
import ForecastingIconFilled from "@redotech/redo-icons/line-chart-up-02_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageIconFilled from "@redotech/redo-icons/package_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-03_filled.svg";
import RefreshIconOutline from "@redotech/redo-icons/refresh-cw-02.svg";
import RepeatIcon from "@redotech/redo-icons/repeat-01.svg";
import RepeatIconFilled from "@redotech/redo-icons/repeat-01_filled.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-03.svg";
import ShoppingBagIconFilled from "@redotech/redo-icons/shopping-bag-03_filled.svg";
import SuppliersIcon from "@redotech/redo-icons/suppliers.svg";
import {
  useHasPermission,
  useIsRedoAdmin,
} from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useLocation } from "react-router-dom";
import { InventoryManagementSuiteStatusesContext } from "../../../../redo-suite/inventory-cloud";
import { CatalogViews } from "./catalog-products";
import { CycleCountViews } from "./cycle-count-views";
import { IntelligentInventoryItem } from "./intelligent-inventory";
import { InventoryAnalyticsItem } from "./inventory-analytics";
import { InventoryLevelsItem } from "./inventory-levels";
import { PurchaseOrdersItem } from "./purchase-orders";
import { ShipmentsItem } from "./shipments";
import { TransfersItem } from "./transfers";

export const InventoryItem = memo(function InventoryItem() {
  const team = useRequiredContext(TeamContext);
  const user = useRequiredContext(UserContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const isRedoEmail = isRedoEmailAddress(user?.email);

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const canAccessTransfers = useHasPermission(ImsPermissions.ACCESS_TRANSFERS);

  const showDashboard = imsEnabled && isRedoEmail;
  const showForecastingOnlyPlans =
    !imsEnabled && (isForecastingEnabled || isForecastingBillingEnabled);
  const showInventoryUpsell =
    !imsEnabled && !isForecastingEnabled && !isForecastingBillingEnabled;

  return (
    <>
      <InventoryDashboardItem hidden={!showDashboard} />
      <InventoryAnalyticsItem />
      <CatalogItem />
      <ForecastingItem />
      <IntelligentInventoryItem />
      <InventoryFolderItem
        canAccessInventory={canAccessInventory}
        hideTransfers={!imsEnabled || !canAccessTransfers}
      />
      {/* Unsubscribed merchants: "Inventory" is an upsell link to onboarding. */}
      {showInventoryUpsell && <InventoryUpsellItem />}
      {/* Forecasting-only merchants (no IMS): show onboarding Plans link. */}
      {showForecastingOnlyPlans && <InventoryPlansItem />}
      <PurchasingItem />
      <RecommerceItem />
    </>
  );
});

const InventoryDashboardItem = memo(function InventoryDashboardItem({
  hidden,
}: {
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/dashboard`,
      }}
      FilledIcon={DashboardIconFilled}
      friendlyName={t("Dashboard", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={hidden}
      id="dashboard"
      UnfilledIcon={DashboardIcon}
    />
  );
});

const CatalogItem = memo(function CatalogItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const base = routes.inventoryManagementBaseUrl;
  const isCatalogEnabled = team.settings.ims?.catalogEnabled ?? false;
  const isCatalogChildSelected =
    pathname.startsWith(`${base}/catalog/`) || pathname === `${base}/catalog`;

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isCatalogChildSelected}
      FilledIcon={CatalogIconFilled}
      friendlyName={t("Catalog", {
        context:
          "inventory navigation section for the merchant product catalog",
      })}
      hidden={!isCatalogEnabled}
      id="catalog"
      isFolder
      UnfilledIcon={CatalogIcon}
    >
      <CatalogViews />
      <CatalogConsignmentItem />
      <CatalogVariantsItem />
    </RedoSecondaryNavItem>
  );
});

const CatalogConsignmentItem = memo(function CatalogConsignmentItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/catalog/consignment`,
      }}
      friendlyName={t("Consignment", {
        context:
          "inventory navigation label for consignment stock owned by a supplier but physically held and sold by the merchant",
      })}
      hidden={!(team._id === "68800d96229f8fc10f4dc1ce")}
      id="catalog-consignment"
    />
  );
});

const CatalogVariantsItem = memo(function CatalogVariantsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/variants`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName={t("Variants", {
        context: "inventory navigation label for product variants",
      })}
      id="product-bins"
      UnfilledIcon={PackageIcon}
    />
  );
});

const ForecastingItem = memo(function ForecastingItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;
  const canAccessForecasting = useHasPermission(
    ImsPermissions.ACCESS_FORECASTING,
  );
  const showForecastingFolder =
    (isForecastingEnabled || isForecastingBillingEnabled) &&
    canAccessForecasting;

  const isForecastingChildSelected = pathname.startsWith(
    `${routes.inventoryManagementBaseUrl}/forecasting`,
  );

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isForecastingChildSelected}
      FilledIcon={ForecastingIconFilled}
      friendlyName={t("Forecasting", {
        context: "inventory navigation label for stock demand forecasting",
      })}
      hidden={!showForecastingFolder}
      id="forecasting"
      isFolder
      UnfilledIcon={ForecastingIcon}
    >
      <ForecastingMonthlyItem />
      <ForecastingRecommendationsItem />
      <ForecastingRulesItem />
    </RedoSecondaryNavItem>
  );
});

const ForecastingMonthlyItem = memo(function ForecastingMonthlyItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/forecasting/monthly`,
      }}
      friendlyName={t("Monthly Forecast", {
        context:
          "inventory navigation label for the monthly stock demand forecast",
      })}
      id="forecasting-monthly"
      isNew
    />
  );
});

const ForecastingRecommendationsItem = memo(
  function ForecastingRecommendationsItem() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/forecasting/recommendations`,
        }}
        beta
        friendlyName={t("Purchasing Recommendations", {
          context: "inventory navigation label for recommended stock purchases",
        })}
        id="forecasting-recommendations"
      />
    );
  },
);

const ForecastingRulesItem = memo(function ForecastingRulesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/forecasting/rules`,
      }}
      beta
      friendlyName={t("Rules", {
        context: "inventory forecasting navigation page for purchasing rules",
      })}
      id="forecasting-rules"
    />
  );
});

const InventoryFolderItem = memo(function InventoryFolderItem({
  canAccessInventory,
  hideTransfers,
}: {
  canAccessInventory: boolean;
  hideTransfers: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;

  const base = routes.inventoryManagementBaseUrl;
  const isInventoryChildSelected =
    pathname.startsWith(`${base}/bill-of-materials`) ||
    pathname.startsWith(`${base}/intelligent-cycle-counts`) ||
    pathname.startsWith(`${base}/adjustments`) ||
    pathname.startsWith(`${base}/products`) ||
    pathname.startsWith(`${base}/barcode-label-templates`) ||
    pathname.startsWith(`${base}/transfers`);

  const showInventoryFolder =
    imsEnabled || isForecastingEnabled || isForecastingBillingEnabled;
  const showInventoryLevels =
    (imsEnabled || isForecastingEnabled || isForecastingBillingEnabled) &&
    canAccessInventory;

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isInventoryChildSelected}
      FilledIcon={PackageIconFilled}
      friendlyName={t("Inventory", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!showInventoryFolder}
      id="inventory"
      isFolder
      UnfilledIcon={PackageIcon}
    >
      <BarcodeLabelTemplatesItem />
      <BillOfMaterialsItem />
      <CycleCountsItem />
      <InventoryAdjustmentsItem />
      {showInventoryLevels && <InventoryLevelsItem />}
      <TransfersItem hidden={hideTransfers} />
    </RedoSecondaryNavItem>
  );
});

const BarcodeLabelTemplatesItem = memo(function BarcodeLabelTemplatesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const isRedoEmployee = useIsRedoAdmin();

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/barcode-label-templates`,
      }}
      FilledIcon={BarcodeLabelIconFilled}
      friendlyName={t("Barcode label templates", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!canAccessInventory || !isRedoEmployee}
      id="barcode-label-templates"
      UnfilledIcon={BarcodeLabelIcon}
    />
  );
});

const BillOfMaterialsItem = memo(function BillOfMaterialsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessBillOfMaterials = useHasPermission(
    ImsPermissions.ACCESS_BILL_OF_MATERIALS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/bill-of-materials`,
      }}
      FilledIcon={LayersIconFilled}
      friendlyName={t("Bill of materials", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!imsEnabled || !canAccessBillOfMaterials}
      id="bill-of-materials"
      UnfilledIcon={LayersIcon}
    />
  );
});

const CycleCountsItem = memo(function CycleCountsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessCycleCounts = useHasPermission(
    ImsPermissions.ACCESS_CYCLE_COUNTS,
  );

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={pathname.startsWith(
        `${routes.inventoryManagementBaseUrl}/intelligent-cycle-counts`,
      )}
      FilledIcon={ClipboardCheckIconFilled}
      friendlyName={t("Cycle counts", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!imsEnabled || !canAccessCycleCounts}
      id="intelligent-cycle-counts"
      isFolder
      isNew
      UnfilledIcon={ClipboardCheckIcon}
    >
      <CycleCountViews />
      <CycleCountRulesItem />
    </RedoSecondaryNavItem>
  );
});

const CycleCountRulesItem = memo(function CycleCountRulesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const canManageCycleCountRules = useHasPermission(
    ImsPermissions.MANAGE_CYCLE_COUNT_RULES,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/intelligent-cycle-counts/rules`,
      }}
      FilledIcon={RepeatIconFilled}
      friendlyName={t("Cycle count rules", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!canManageCycleCountRules}
      id="cycle-count-rules"
      UnfilledIcon={RepeatIcon}
    />
  );
});

const InventoryAdjustmentsItem = memo(function InventoryAdjustmentsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/adjustments`,
      }}
      FilledIcon={InventoryAdjustmentsIcon}
      friendlyName={t("Inventory adjustments", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!imsEnabled || !canAccessInventory}
      id="inventory-adjustments"
      UnfilledIcon={InventoryAdjustmentsIcon}
    />
  );
});

/** Upsell link shown to merchants without any inventory subscription. */
const InventoryUpsellItem = memo(function InventoryUpsellItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/onboarding`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName={t("Inventory", {
        context: "inventory and purchasing navigation label",
      })}
      id="inventory-upsell"
      UnfilledIcon={PackageIcon}
    />
  );
});

/** Onboarding link shown to forecasting-only merchants (no IMS). */
const InventoryPlansItem = memo(function InventoryPlansItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/onboarding`,
      }}
      FilledIcon={PackageIconFilled}
      friendlyName={t("Plans", {
        context: "inventory navigation page for inventory planning",
      })}
      id="inventory-plans"
      UnfilledIcon={PackageIcon}
    />
  );
});

const PurchasingItem = memo(function PurchasingItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );
  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );
  const canAccessShipments = useHasPermission(ImsPermissions.ACCESS_SHIPMENTS);

  const base = routes.inventoryManagementBaseUrl;
  const isPurchasingChildSelected =
    pathname.startsWith(`${base}/purchase-orders`) ||
    pathname.startsWith(`${base}/shipments`) ||
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isPurchasingChildSelected}
      FilledIcon={ShoppingBagIconFilled}
      friendlyName={t("Purchasing", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!imsEnabled}
      id="purchasing"
      isFolder
      UnfilledIcon={ShoppingBagIcon}
    >
      {canAccessPurchaseOrders && <PurchaseOrdersItem />}
      {canAccessShipments && <ShipmentsItem />}
      <SuppliersItem />
    </RedoSecondaryNavItem>
  );
});

const SuppliersItem = memo(function SuppliersItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const canAccessSuppliers = useHasPermission(ImsPermissions.ACCESS_SUPPLIERS);

  const base = routes.inventoryManagementBaseUrl;
  const isSuppliersChildSelected =
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isSuppliersChildSelected}
      FilledIcon={SuppliersIcon}
      friendlyName={t("Suppliers", {
        context: "inventory and purchasing navigation label",
      })}
      hidden={!canAccessSuppliers}
      id="suppliers"
      isFolder
      UnfilledIcon={SuppliersIcon}
    >
      <AllSuppliersItem />
      <PoTemplatesItem />
    </RedoSecondaryNavItem>
  );
});

const AllSuppliersItem = memo(function AllSuppliersItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/suppliers`,
      }}
      friendlyName={t("All suppliers", {
        context: "inventory and purchasing navigation label",
      })}
      id="suppliers-all"
    />
  );
});

const PoTemplatesItem = memo(function PoTemplatesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/document-templates`,
      }}
      friendlyName={t("PO templates", {
        context: "inventory navigation label for purchase order templates",
      })}
      hidden={!canAccessPurchaseOrders}
      id="document-templates"
    />
  );
});

const RecommerceItem = memo(function RecommerceItem() {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const { pathname } = useLocation();

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;
  const isRecommerceChildSelected =
    pathname.includes("/liquidation/") || pathname.endsWith("/liquidation");

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isRecommerceChildSelected}
      FilledIcon={RefreshIcon}
      friendlyName={t("Recommerce", {
        context:
          "inventory navigation label for resale and recommerce listings",
      })}
      hidden={!isMarketplaceEnabled}
      id="recommerce"
      isFolder
      UnfilledIcon={RefreshIconOutline}
    >
      <LiquidationDashboardItem />
      <LiquidationAllListingsItem />
    </RedoSecondaryNavItem>
  );
});

const LiquidationDashboardItem = memo(function LiquidationDashboardItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/liquidation/dashboard`,
      }}
      friendlyName={t("Dashboard", {
        context: "inventory and purchasing navigation label",
      })}
      id="liquidation-dashboard"
    />
  );
});

const LiquidationAllListingsItem = memo(function LiquidationAllListingsItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${routes.inventoryManagementBaseUrl}/liquidation/products`,
      }}
      friendlyName={t("All listings", {
        context: "inventory and purchasing navigation label",
      })}
      id="liquidation-all-products"
    />
  );
});
